import { useEffect, useState } from 'react';
import { useApiData } from 'lib/api';
import { useDispatch, useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import {
  Badge,
  DisplayFormattedDate,
  EmptyIndicator,
  FlexContainer,
  LoadingIndicator,
  MoreOptions,
  NoEmailAddress,
  Pagination,
  SearchForm,
  User
} from 'components/common';
import {
  Box,
  BoxOverflowWrapper,
  BoxTable,
  BoxTableCell,
  BoxTableHeader,
  BoxTableRow
} from 'components/common/box';
import {
  AttributeDisplayValue,
  UserCustomAttributeDisplayValue
} from 'components/common/customAttributes';
import { GroupAttributeUsersInvite } from '.';

import type { EmailTemplateData, UserCustomAttribute } from 'shared-types';

interface Props {
  emailTemplates: EmailTemplateData[];
  emailTemplatesDefault: EmailTemplateData;
}

const GroupAttributesUsers = ({
  emailTemplates,
  emailTemplatesDefault
}: Props) => {
  const { dispatch } = useDispatch();

  const {
    language,
    messages,
    pageState: { group },
    serverConfig: { isDemoEnvironment, userCustomAttributes }
  } = useReduxState();

  if (isDemoEnvironment) return null;

  const [isMounted, setIsMounted] = useState(false);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');

  const { data, loading, updateData } = useApiData({
    url:
      `/admin/groups/${group.id}/attribute-users?page=${page}` +
      (search ? `&search=${search}` : '')
  });

  // update data if group user attributes change
  useEffect(() => {
    if (!isMounted) {
      setIsMounted(true);
      return;
    }
    updateData({ showLoading: true });
  }, [group?.userAttributes]);

  const updatePage = ({ page }) => setPage(page);

  const attributesByKey: { [key: string]: UserCustomAttribute } =
    userCustomAttributes
      ? userCustomAttributes.reduce((obj, attr) => {
          obj[attr.key] = attr;
          return obj;
        }, {})
      : {};

  const uniqueAttributes =
    group.userAttributes && group.userAttributes.length
      ? group.userAttributes
          .flatMap((attr) => attr)
          .reduce((arr, attribute) => {
            if (!arr.includes(attribute.key)) arr.push(attribute.key);
            return arr;
          }, [])
      : [];

  return (
    <div className="attribute-users">
      <Box
        padding={0}
        title={messages.groupsUserAttributesUsers}
        headerContent={
          data?.users?.length ? (
            <GroupAttributeUsersInvite
              emailTemplates={emailTemplates}
              emailTemplatesDefault={emailTemplatesDefault}
              updateUsers={updateData}
            />
          ) : null
        }
        subHeaderContent={
          <div style={{ maxWidth: 300, width: '100%' }}>
            <SearchForm
              placeholderText={messages.settingsSearchForUser}
              onClear={() => setSearch('')}
              onError={dispatch.handleErrorDispatch}
              onSearch={(value: string) => setSearch(value)}
              size="header"
            />
          </div>
        }
      >
        {loading ? (
          <LoadingIndicator />
        ) : data?.users?.length ? (
          <BoxOverflowWrapper>
            <BoxTable>
              <BoxTableHeader hideAtBreakpoint={breakpoints.medium}>
                <BoxTableCell>{messages.commonUser}</BoxTableCell>
                <BoxTableCell>{messages.commonInviteEmail}</BoxTableCell>
                {uniqueAttributes.map((key) => (
                  <BoxTableCell key={key}>
                    {key === 'emailDomain'
                      ? messages.userAttributesEmailDomain
                      : key === 'accountType'
                      ? messages.usersAccountType
                      : attributesByKey[key]
                      ? attributesByKey[key].titleTranslations?.[language] ||
                        attributesByKey[key].title
                      : key}
                  </BoxTableCell>
                ))}
                <BoxTableCell></BoxTableCell>
              </BoxTableHeader>

              {data.users.map((user) => (
                <BoxTableRow key={user.id}>
                  <BoxTableCell cellBreakpoint={breakpoints.medium}>
                    <User enableProfileLink={true} user={user} />
                  </BoxTableCell>
                  <BoxTableCell
                    cellBreakpoint={breakpoints.medium}
                    emptyValue="-"
                    {...(user.inviteEmailDate ? { icon: 'envelope' } : {})}
                  >
                    {user.inviteEmailDate ? (
                      <DisplayFormattedDate date={user.inviteEmailDate} />
                    ) : user.email ? null : (
                      <NoEmailAddress />
                    )}
                  </BoxTableCell>

                  {uniqueAttributes.map((key) => (
                    <BoxTableCell key={key} cellBreakpoint={breakpoints.medium}>
                      {key === 'emailDomain' ? (
                        user.email?.slice(user?.email?.lastIndexOf('@') + 1)
                      ) : key === 'accountType' ? (
                        <FlexContainer style={{ gap: 5 }}>
                          <Badge
                            size="small"
                            text={
                              user.roles.includes('superadmin')
                                ? messages.usersSuperadmin
                                : user.roles.includes('admin')
                                ? messages.usersAdmin
                                : messages.commonUser
                            }
                          />

                          {user.roles.includes('supervisor') ? (
                            <Badge
                              size="small"
                              text={messages.adminSupervisor}
                            />
                          ) : null}
                        </FlexContainer>
                      ) : attributesByKey[key] ? (
                        <UserCustomAttributeDisplayValue
                          attribute={attributesByKey[key]}
                          value={user.customAttributes[key]}
                        />
                      ) : (
                        <AttributeDisplayValue
                          value={user.customAttributes[key]}
                        />
                      )}
                    </BoxTableCell>
                  ))}
                  <BoxTableCell
                    shrink={true}
                    verticalBreakpoint={breakpoints.medium}
                  >
                    {user.email ? (
                      <MoreOptions renderAsFixed={true}>
                        <GroupAttributeUsersInvite
                          emailTemplates={emailTemplates}
                          emailTemplatesDefault={emailTemplatesDefault}
                          targetUser={user}
                          updateUsers={updateData}
                        />
                      </MoreOptions>
                    ) : null}
                  </BoxTableCell>
                </BoxTableRow>
              ))}
            </BoxTable>
          </BoxOverflowWrapper>
        ) : (
          <EmptyIndicator icon="user" title={messages.commonNoUsers} />
        )}
      </Box>

      <Pagination
        currentPage={page}
        numberOfPages={data?.pages}
        onPageChange={updatePage}
      />
    </div>
  );
};

export default GroupAttributesUsers;
